/**
 * Invite Form Component
 *
 * Form for sending new invites.
 *
 * @fileoverview Invite form component
 */

import React, { useState } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Input,
  Button,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import type { CreateInviteInput } from '../../types/admin';

interface InviteFormProps {
  loading: boolean;
  error: string | null;
  onSubmit: (data: CreateInviteInput) => void;
  onCancel: () => void;
}

interface FormErrors {
  email?: string;
  expiresAt?: string;
}

/**
 * Invite Form Component
 *
 * Handles sending new invites.
 */
export const InviteForm: React.FC<InviteFormProps> = ({
  loading,
  error,
  onSubmit,
  onCancel,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Calculate default expiry date (7 days from now)
  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    email: '',
    expiresAt: getDefaultExpiryDate(),
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Must be a valid email address';
        }
        break;
      case 'expiresAt':
        if (value) {
          const expiryDate = new Date(value);
          if (expiryDate <= new Date()) {
            return 'Expiration date must be in the future';
          }
        }
        break;
    }
    return undefined;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) {
        newErrors[key as keyof FormErrors] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    setTouched(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );

    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const data: CreateInviteInput = {
      email: formData.email.trim().toLowerCase(),
      ...(formData.expiresAt && { expiresAt: new Date(formData.expiresAt).toISOString() }),
    };

    onSubmit(data);
  };

  return (
    <Box
      as="form"
      onSubmit={handleSubmit}
      bg={bgColor}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      p={6}
    >
      <VStack spacing={6} align="stretch">
        <FormControl isRequired isInvalid={touched.email && !!errors.email}>
          <FormLabel>Email Address</FormLabel>
          <Input
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="newuser@example.com"
          />
          <FormHelperText>
            An invitation email will be sent to this address
          </FormHelperText>
          <FormErrorMessage>{errors.email}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={touched.expiresAt && !!errors.expiresAt}>
          <FormLabel>Expiration Date</FormLabel>
          <Input
            name="expiresAt"
            type="date"
            value={formData.expiresAt}
            onChange={handleChange}
            onBlur={handleBlur}
            min={new Date().toISOString().split('T')[0]}
          />
          <FormHelperText>
            The invite will expire on this date (defaults to 7 days from now)
          </FormHelperText>
          <FormErrorMessage>{errors.expiresAt}</FormErrorMessage>
        </FormControl>

        {/* Error message */}
        {error && (
          <Box color="red.500" fontSize="sm">
            {error}
          </Box>
        )}

        {/* Actions */}
        <Box pt={4} display="flex" gap={4} justifyContent="flex-end">
          <Button variant="ghost" onClick={onCancel} isDisabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={loading}
            loadingText="Sending..."
          >
            Send Invite
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};

export default InviteForm;
